/**
 * Kiracı yapılandırması.
 *
 * ### ⚠️ Artık API'den gelir, HTML'den DEĞİL
 * Önceden Worker aynı origin'den HTML servis ediyor ve HTMLRewriter ile
 * `window.__CENTVOX__`'u **ilk boyamadan önce** enjekte ediyordu (Rapor 14
 * §2.3). İstemci Cloudflare Pages'e taşınınca HTML'i Worker üretmiyor;
 * yapılandırma `GET /api/v1/bootstrap` ile alınır.
 *
 * ⚠️ **Bedeli:** marka bir gidiş-dönüş sonrası gelir, yani kısa bir marka
 *    atlaması (FOUC) olasıdır. `sessionStorage` önbelleği bunu ilk açılıştan
 *    sonra ortadan kaldırır.
 */
export interface CentvoxBootstrap {
  productName: string;
  wsOrigin: string;
  /** ⭐ API kökü — Pages ile Worker AYRI origin'lerdedir. */
  apiOrigin: string;
  /**
   * ⭐ **Gerçek katılımcı tavanı** — sunucudan gelir.
   *
   * ⚠️ İstemci bunu HESAPLAYAMAZ: tavan, ürün planı ile platform katmanının
   *    kesişimidir ve ikincisini yalnız sunucu bilir. Sabit bir sayı yazmak,
   *    ürünün yapamadığı bir şeyi vaat etmekti (Rapor 08 §9).
   */
  maxParticipants: number;
  turnstileSiteKey: string;
  environment: "local" | "production";
  protocolVersion: number;
  /** ⭐ Kiracı markası (F2-06). `null` = varsayılan tema. */
  brandColor?: string | null;
  logoUrl?: string | null;
}

/**
 * ⭐ **API kökü derleme zamanında sabitlenir** (`VITE_API_ORIGIN`).
 *
 * ⚠️ Çalışma anında keşfedilemez: yapılandırmayı almak için zaten API'ye
 *    gitmek gerekir — tavuk-yumurta. Pages ortam değişkeni olarak verilir.
 *
 * ⚠️ Sondaki `/` **temizlenir**: `https://api.x/` + `/api/v1/...` çift eğik
 *    çizgi üretir ve bazı yönlendiriciler 404 döner.
 */
export const API_ORIGIN: string = resolveApiOrigin();

/**
 * ⚠️ `location` **modül seviyesinde doğrudan okunmaz**: DOM'u olmayan her
 *    bağlamda (birim testi, SSR, worker) modül daha içe aktarılırken
 *    `ReferenceError` ile patlar ve o dosyaya dokunan tüm testler düşer.
 */
function resolveApiOrigin(): string {
  const configured = import.meta.env["VITE_API_ORIGIN"] as string | undefined;
  if (typeof configured === "string" && configured.length > 0) {
    return configured.replace(/\/+$/, "");
  }
  return typeof location === "undefined" ? "" : location.origin.replace(/\/+$/, "");
}

const FALLBACK: CentvoxBootstrap = {
  productName: "Centvox",
  wsOrigin: API_ORIGIN.replace(/^http/, "ws"),
  apiOrigin: API_ORIGIN,
  // ⚠️ Yedek değer **en dar** olan: sunucuya ulaşılamadığında abartmak yerine
  //    az söylemek doğru taraftır.
  maxParticipants: 8,
  turnstileSiteKey: "",
  environment: "local",
  protocolVersion: 1,
  brandColor: null,
  logoUrl: null,
};

const CACHE_KEY = "centvox:bootstrap";
let loaded: CentvoxBootstrap = FALLBACK;

/**
 * Yapılandırmayı **senkron** döndürür.
 *
 * ⚠️ İmza bilinçli olarak senkron kaldı: onlarca çağrı yeri var ve hepsini
 *    async yapmak, render yolunu await'lerle dolduracaktı. `loadBootstrap()`
 *    açılışta bir kez çağrılır ve değeri buraya yerleştirir.
 */
export function bootstrap(): CentvoxBootstrap {
  return loaded;
}

/**
 * Açılışta bir kez çağrılır.
 *
 * ⚠️ Başarısızlık uygulamayı DÜŞÜRMEZ: varsayılan marka ile devam edilir.
 *    Bilinmeyen bir alan adında katılım sayfasının hiç açılmaması, yanlış
 *    logo göstermekten çok daha kötüdür.
 */
export async function loadBootstrap(): Promise<CentvoxBootstrap> {
  // ⚠️ Önce önbellek: ikinci sayfa açılışında marka atlaması olmaz.
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached !== null) loaded = { ...FALLBACK, ...(JSON.parse(cached) as CentvoxBootstrap) };
  } catch {
    /* önbellek okunamadı — sorun değil */
  }

  try {
    const res = await fetch(
      `${API_ORIGIN}/api/v1/bootstrap?host=${encodeURIComponent(location.hostname)}`,
    );
    if (res.ok) {
      loaded = { ...FALLBACK, ...((await res.json()) as CentvoxBootstrap) };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(loaded));
      } catch {
        /* kota dolu olabilir */
      }
    }
  } catch {
    /* ağ hatası — varsayılanla devam */
  }

  applyBranding(loaded);
  return loaded;
}

/**
 * ⚠️ Marka rengi bir **CSS değişkenine** yazılır, satır içi stillere değil.
 *    `tokens.css` her rengi `--brand-accent` üzerinden alır.
 *
 * ⚠️ Renk istemcide **yeniden doğrulanır**: sunucu süzse bile, önbellekten
 *    gelen bir değere güvenmek `style` içine keyfi metin yazmak olurdu.
 */
function applyBranding(b: CentvoxBootstrap): void {
  if (typeof b.brandColor === "string" && /^#[0-9a-f]{6}$/i.test(b.brandColor)) {
    document.documentElement.style.setProperty("--brand-accent", b.brandColor);
  }
  if (b.productName.length > 0) document.title = b.productName;
}

/**
 * ⭐ **Davet sırrı URL FRAGMENT'ından okunur** (Rapor 03 §3.1).
 *
 * Tarayıcı `#` sonrasını sunucuya **göndermez** → CDN, erişim logu, Referer ve
 * reverse proxy sırrı görmez. İstemci onu okur ve `POST …/join` **gövdesinde**
 * yollar.
 *
 * ⚠️ Okur okumaz `history.replaceState` ile temizlenir: tarayıcı geçmişi, ekran
 *    görüntüsü, pano ve "linki paylaş" davranışı hâlâ risktir; fragment tek
 *    başına yeterli DEĞİLDİR (lobi + PIN + süre sınırı ile birlikte kullanılır).
 */
export function takeInviteSecret(): string | null {
  const hash = location.hash;
  const m = /^#s=([A-Za-z0-9_-]{43})$/.exec(hash);
  if (!m?.[1]) return null;
  history.replaceState(null, "", location.pathname + location.search);
  return m[1];
}

/** `/j/{meetingId}` yolundan toplantı kimliğini çıkarır. */
export function meetingIdFromPath(): string | null {
  const m = /^\/j\/([0-9a-f]{32})$/.exec(location.pathname);
  return m?.[1] ?? null;
}
